/**
 * N4 — Launch Calendar month color coding.
 *
 * Each month cell is colored according to its forecast quantity so
 * users can spot rush months at a glance:
 *
 *   Green  — low load
 *   Blue   — medium
 *   Orange — high
 *   Red    — critical
 *
 * The thresholds are documented as a single constant so product can
 * tune them without hunting through the render code.
 */

export interface MonthColorTier {
  label: string;
  color: string;
  upperBound: number; // inclusive upper limit for this tier
}

export const MONTH_COLOR_THRESHOLDS: MonthColorTier[] = [
  { label: "Low",      color: "#10b981", upperBound: 5_000 },
  { label: "Medium",   color: "#2563eb", upperBound: 15_000 },
  { label: "High",     color: "#f97316", upperBound: 30_000 },
  { label: "Critical", color: "#ef4444", upperBound: Infinity },
];

/**
 * Resolve the color tier that applies to the given forecast quantity.
 * Returns the first tier whose upperBound >= qty. Always returns a
 * tier because the last one is open-ended (Infinity).
 */
export function resolveMonthColorTier(qty: number): MonthColorTier {
  // NaN / undefined → Low (safe default). Negative → clamped to 0.
  // Infinity → last tier (Critical).
  const value = Number.isNaN(qty) ? 0 : Math.max(0, qty);
  for (const tier of MONTH_COLOR_THRESHOLDS) {
    if (value <= tier.upperBound) return tier;
  }
  return MONTH_COLOR_THRESHOLDS[MONTH_COLOR_THRESHOLDS.length - 1];
}
